<template>
  <div
    class="vue-google-autocomplete-container"
    style="position: relative; width: 100%;"
  >
    <input
      ref="autocomplete"
      type="text"
      :id="id"
      :class="classname"
      :placeholder="placeholder"
      v-model="searchQuery"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      autocomplete="off"
    />

    <ul
      v-if="showDropdown && predictions.length > 0"
      class="pac-container-custom"
    >
      <li
        v-for="(prediction, index) in predictions"
        :key="index"
        @mousedown="selectPrediction(prediction)"
        class="pac-item-custom"
      >
        <span class="pac-item-query">{{
          prediction.structured_formatting.main_text
        }}</span>
        <span class="pac-secondary-text">{{
          prediction.structured_formatting.secondary_text
        }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "VueGoogleAutocomplete",
  props: {
    id: { type: String, required: true },
    classname: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    types: { type: String, default: "address" },
    country: { type: [String, Array], default: null },
    options: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      searchQuery: "",
      predictions: [],
      showDropdown: false,
      autocompleteService: null,
      geocoder: null
    };
  },
  mounted() {
    this.initGoogleServices();
  },
  methods: {
    initGoogleServices() {
      if (
        typeof window.google !== "undefined" &&
        window.google.maps &&
        window.google.maps.places
      ) {
        this.autocompleteService = new window.google.maps.places.AutocompleteService();
        this.geocoder = new window.google.maps.Geocoder();
      } else {
        // Retry if the global Google script hasn't quite finished loading yet
        setTimeout(this.initGoogleServices, 300);
      }
    },
    onInput() {
      if (!this.searchQuery || this.searchQuery.length < 3) {
        this.predictions = [];
        return;
      }

      const request = {
        input: this.searchQuery,
        ...this.options
      };

      if (this.types) {
        request.types = [this.types];
      }
      if (this.country) {
        request.componentRestrictions = { country: this.country };
      }

      this.autocompleteService.getPlacePredictions(
        request,
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            this.predictions = predictions;
            this.showDropdown = true;
          } else {
            this.predictions = [];
          }
        }
      );
    },
    selectPrediction(prediction) {
      this.searchQuery = prediction.description;
      this.showDropdown = false;

      if (!this.geocoder) return;

      // Fetch details to match the exact payload format of the old plugin
      this.geocoder.geocode(
        { placeId: prediction.place_id },
        (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
            const result = results[0];
            const addressData = this.parseAddressComponents(
              result.address_components
            );
            addressData.latitude = result.geometry.location.lat();
            addressData.longitude = result.geometry.location.lng();

            // Emit the exact event your parent forms are already listening for
            this.$emit("placechanged", addressData, result, this.id);
          }
        }
      );
    },
    onFocus() {
      if (this.predictions.length > 0) this.showDropdown = true;
    },
    onBlur() {
      // Small timeout allows the mousedown event on the dropdown item to fire first
      setTimeout(() => {
        this.showDropdown = false;
      }, 200);
    },
    parseAddressComponents(components) {
      const address = {};
      components.forEach(c => {
        const type = c.types[0];
        if (type === "street_number") address.street_number = c.long_name;
        if (type === "route") address.route = c.long_name;
        if (type === "locality") address.locality = c.long_name;
        if (type === "administrative_area_level_1")
          address.administrative_area_level_1 = c.long_name;
        if (type === "country") address.country = c.long_name;
        if (type === "postal_code") address.postal_code = c.long_name;
      });
      return address;
    }
  }
};
</script>

<style scoped>
.new-delivery-address {
  width: 100%;
  border: 1px solid var(--v-grey-base);
  border-radius: 5px;
  padding: 12px;
}
/* Custom styling to replicate Google's native 'pac-container' menu styling */
.pac-container-custom {
  background-color: #fff;
  position: absolute;
  z-index: 1050;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  font-family: Roboto, Arial, sans-serif;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
  overflow: hidden;
  margin: 4px 0 0 0;
  padding: 0;
  width: 100%;
  list-style-type: none;
}
.pac-item-custom {
  cursor: pointer;
  padding: 8px 12px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  text-align: left;
  border-top: 1px solid #e6e6e6;
  font-size: 13px;
}
.pac-item-custom:first-child {
  border-top: none;
}
.pac-item-custom:hover {
  background-color: #f5f5f5;
}
.pac-item-query {
  font-size: 14px;
  padding-right: 5px;
  color: #333;
  font-weight: 500;
}
.pac-secondary-text {
  color: #777;
}
</style>
